import React, { memo, type ReactElement, type RefObject } from "react";
import type { ValidFormPanel } from "src/components/Form/Form.types.ts";

import { type FieldPath, type FieldValues } from "react-hook-form";
import { ErrorBoundary } from "react-error-boundary";
import { FormPanelFallback } from "src/components/Form/FormPanelFallback.tsx";
import { DefaultFormPanel } from "src/components/Form/panels/DefaultFormPanel/DefaultFormPanel.tsx";
import { CommentsPanel } from "src/components/Form/panels/CommentsPanel/CommentsPanel.tsx";
import { TransferPanel } from "src/components/Form/panels/TransferPanel/TransferPanel.tsx";
import { CommentsPanelV2 } from "src/components/Form/panels/CommentsPanelV2/CommentsPanelV2.tsx";
import { useIsPanelHidden } from "src/components/Form/hooks/useIsPanelHidden.ts";
import { ContactsPanel } from "src/components/Form/panels/ContactsPanel/ContactsPanel.tsx";
import { PricePanel } from "src/components/Form/panels/PricePanel/PricePanel.tsx";

export type FormPanelProps<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  panel: ValidFormPanel<FormValues, FieldNames>;
  panelKey: string;
  formScrollContainerRef?: RefObject<HTMLElement>;
};

export const FormPanel = memo(({ panel, panelKey, formScrollContainerRef }) => {
  const { isHidden } = useIsPanelHidden({
    panel,
  });

  if (isHidden) {
    return null;
  }
  if (panel.type === "custom") {
    return (
      <ErrorBoundary FallbackComponent={FormPanelFallback}>
        <panel.Component formScrollContainerRef={formScrollContainerRef} />
      </ErrorBoundary>
    );
  }

  if (panel.type === "comments") {
    return (
      <ErrorBoundary FallbackComponent={FormPanelFallback}>
        <CommentsPanel onSave={panel.onCreateComment} notes={panel.notes} />
      </ErrorBoundary>
    );
  }

  if (panel.type === "comments-v2") {
    return (
      <ErrorBoundary FallbackComponent={FormPanelFallback}>
        <CommentsPanelV2 resource={panel.resource} />
      </ErrorBoundary>
    );
  }

  if (panel.type === "transfer") {
    return (
      <ErrorBoundary FallbackComponent={FormPanelFallback}>
        <TransferPanel {...panel} />
      </ErrorBoundary>
    );
  }

  if (panel.type === "contacts") {
    return (
      <ErrorBoundary FallbackComponent={FormPanelFallback}>
        <ContactsPanel />
      </ErrorBoundary>
    );
  }

  if (panel.type === "price") {
    return (
      <ErrorBoundary FallbackComponent={FormPanelFallback}>
        <PricePanel />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={FormPanelFallback}>
      <DefaultFormPanel
        title={panel.title}
        helperText={panel.helperText}
        panelKey={panelKey}
        fields_order={panel.fields_order}
        css={panel.css}
      />
    </ErrorBoundary>
  );
}) as <
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
>({
  panel,
  panelKey,
}: FormPanelProps<FormValues, FieldNames>) => ReactElement | null;
